import { useCallback, useEffect, useState } from 'react';
import { SlideOut, Card, Checkbox, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { IntegrationProviderLogo } from '../../components';
import { listDistribution, setWorkspaceIncluded } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

/**
 * Workspace manage panel (CT-4209): all connected integrations with include/
 * exclude toggles for THIS workspace. Writes the SAME distribution settings as
 * the integration-first panel (CT-4208) via setWorkspaceIncluded. Changes apply
 * immediately (mock); Select/Deselect all act on the toggleable integrations
 * (those in "all" mode are visible everywhere and can't be excluded here).
 */
const WorkspaceManagePanel = ({ workspace, onClose, onChanged }) => {
  const { t } = useTranslation('admin-studio');
  const [rows, setRows] = useState([]);

  // Pure fetch (no setState) — keeping the state update out of the callback and
  // in the effect's async continuation avoids the compiler inlining a synchronous
  // setState into the effect (react-hooks/set-state-in-effect).
  const fetchRows = useCallback(async () => {
    const res = await listDistribution();
    return validateApiResponse(res).body ?? [];
  }, []);

  useEffect(() => {
    let active = true;
    fetchRows().then((data) => {
      if (active) setRows(data);
    });
    return () => {
      active = false;
    };
  }, [fetchRows]);

  const includes = (row) => row.workspaces_mode === 'all' || row.workspaces.includes(workspace);

  const toggle = async (row) => {
    await setWorkspaceIncluded(row.slug, workspace, !includes(row));
    setRows(await fetchRows());
    onChanged?.();
  };

  const setAll = async (included) => {
    const toggleable = rows.filter((r) => r.workspaces_mode !== 'all');
    await Promise.all(toggleable.map((r) => setWorkspaceIncluded(r.slug, workspace, included)));
    setRows(await fetchRows());
    onChanged?.();
  };

  const toggleable = rows.filter((r) => r.workspaces_mode !== 'all');
  const includedCount = toggleable.filter((r) => r.workspaces.includes(workspace)).length;
  const allSelected = toggleable.length > 0 && includedCount === toggleable.length;
  const noneSelected = includedCount === 0;

  return (
    <SlideOut
      isOpen={Boolean(workspace)}
      onClose={onClose}
      renderHeader={(titleId) => (
        <SlideOut.Header
          titleId={titleId}
          title={workspace}
          subtitle={t('integrations.workspaces.manageTitle')}
          showClose
          onClose={onClose}
        />
      )}
      showCta
      cta={{ primary: { label: t('integrations.workspaces.done'), onClick: onClose } }}
    >
      <Card>
        <Card.Body>
          <div className="mb-3 flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setAll(false)}
              disabled={noneSelected}
            >
              {t('integrations.workspaces.deselectAll')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setAll(true)}
              disabled={allSelected}
            >
              {t('integrations.workspaces.selectAll')}
            </Button>
          </div>

          <ul className="flex flex-col divide-y divide-edge">
            {rows.map((row) => (
              <li key={row.slug} className="flex items-center gap-3 py-2">
                <IntegrationProviderLogo slug={row.slug} name={row.name} size="small" />
                <span className="flex-1 truncate text-sm text-content">{row.name}</span>
                <Checkbox
                  checked={includes(row)}
                  disabled={row.workspaces_mode === 'all'}
                  onChange={() => toggle(row)}
                  aria-label={row.name}
                />
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-content-muted">{t('integrations.workspaces.allNote')}</p>
        </Card.Body>
      </Card>
    </SlideOut>
  );
};

export default WorkspaceManagePanel;
